<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div v-if="error" class="box error">
  <h2> {{ $t('error') }} </h2>
  <p>{{ $t('error-loading-image') }}</p>
  <p v-if="errorBadImageProject">{{ $t('error-loading-image-bad-project') }}</p>
</div>
<div v-else class="cytomine-viewer">
  <b-loading :is-full-page="false" :active="loading" />

  <!-- <div class="ae-sidebar">
    <app-engine-sidebar></app-engine-sidebar>
  </div> -->

  <div v-if="!loading" class="maps-wrapper">
    <div class="map-cell"
      v-for="(cell, i) in cells"
      :key="i"
      :style="`height:${elementHeight}%; width:${elementWidth}%;`"
      :class="{highlighted: cell && cell.highlighted}"
    >
      <cytomine-image
        v-if="cell && cell.image && cell.slices"
        :index="cell.index"
        :key="`${cell.index}-${cell.image.id}`"
        @close="closeMap(cell.index)"
      />
    </div>

    <!-- Emit event when a hotkey is pressed (to rework once https://github.com/iFgR/vue-shortkey/issues/78 is implemented) -->
    <div class="hidden" v-shortkey.once="shortkeysMapping" @shortkey="shortkeyEvent"></div>

  </div>

  <!-- Draw Tools Toolbar (Left Side) -->
  <div v-if="!loading && activeImageIndex !== null && configUI['project-tools-main']" class="draw-tools-floating">
    <draw-tools :index="activeImageIndex" :key="activeImageIndex" @screenshot="takeScreenshot" />
  </div>

  <!-- Floating Toolbar -->
  <div v-if="!loading && activeImageIndex !== null" class="floating-toolbar">
    <div
      class="toolbar-button close-button"
      @click="closeViewer"
      v-tooltip="$t('button-close')"
    >
      <i class="fas fa-times-circle"></i>
    </div>

    <template v-if="isPanelDisplayed('hide-tools')">
      <div 
        v-if="isPanelDisplayed('info')"
        v-tooltip="$t('information')"
        class="toolbar-button" 
        :class="{active: ['info', 'metadata'].includes(activePanel)}"
        @click="togglePanel('info')"
      >
        <i class="fas fa-info"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('digital-zoom')" 
        v-tooltip="$t('digital-zoom')"
        class="toolbar-button" 
        :class="{active: activePanel === 'digital-zoom'}"
        @click="togglePanel('digital-zoom')"
      >
        <i class="fas fa-search"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('rotation-panel')" 
        v-tooltip="$t('rotation-panel')"
        class="toolbar-button" 
        :class="{active: activePanel === 'rotation-panel'}"
        @click="togglePanel('rotation-panel')"
      >
        <i class="fas fa-sync-alt"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('link') && nbImages > 1" 
        v-tooltip="$t('link-images')"
        class="toolbar-button" 
        :class="{active: activePanel === 'link'}"
        @click="togglePanel('link')"
      >
        <i class="fas fa-link"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('color-manipulation')" 
        v-tooltip="$t('colors')"
        class="toolbar-button" 
        :class="{active: activePanel === 'colors'}"
        @click="togglePanel('colors')"
      >
        <i class="fas fa-adjust"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('image-layers')" 
        v-tooltip="$t('annotation-layers')"
        class="toolbar-button" 
        :class="{active: activePanel === 'layers'}"
        @click="togglePanel('layers')"
      >
        <i class="fas fa-copy"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('ontology') && terms && terms.length > 0" 
        v-tooltip="$t('ontology')"
        class="toolbar-button" 
        :class="{active: activePanel === 'ontology'}"
        @click="togglePanel('ontology')"
      >
        <i class="fas fa-hashtag"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('property')" 
        v-tooltip="$t('properties')"
        class="toolbar-button" 
        :class="{active: activePanel === 'properties'}"
        @click="togglePanel('properties')"
      >
        <i class="fas fa-tag"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('follow')" 
        v-tooltip="$t('broadcast')"
        class="toolbar-button" 
        :class="{active: activePanel === 'follow'}"
        @click="togglePanel('follow')"
      >
        <i class="fas fa-street-view"></i>
      </div>

      <div 
        v-if="isPanelDisplayed('review') && canEditActiveImage" 
        v-tooltip="$t('review')"
        class="toolbar-button" 
        :class="{active: activePanel === 'review'}"
        @click="togglePanel('review')"
      >
        <i class="fas fa-check-circle"></i>
      </div>
    </template>

    <!-- Panel Options (positioned relative to toolbar) -->
    <information-panel
      class="panel-options"
      v-show="activePanel === 'info'"
      :index="activeImageIndex"
      @openMetadata="togglePanel('metadata')"
      @openCalibration="calibrationModal = true"
    />
    <div v-show="activePanel === 'metadata'">
      <metadata-panel class="panel-metadata" :index="activeImageIndex" @close-metadata="togglePanel('info')" />
    </div>
    <digital-zoom 
      class="panel-options" 
      v-show="activePanel === 'digital-zoom'" 
      :index="activeImageIndex"
      @resetZoom="resetZoom"
      @fitZoom="fitZoom" 
    />
    <rotation-panel
      class="panel-options" 
      v-show="activePanel === 'rotation-panel'" 
      :index="activeImageIndex"
    />
    <link-panel class="panel-options" v-show="activePanel === 'link'" :index="activeImageIndex" />
    <color-manipulation class="panel-options" v-show="activePanel === 'colors'" :index="activeImageIndex" />
    <layers-panel 
      class="panel-options"
      v-show="activePanel === 'layers'"
      :key="`layers-${activeImageIndex}`"
      :index="activeImageIndex"
      :layers-to-preload="layersToPreload"
    />
    <ontology-panel class="panel-options" v-show="activePanel === 'ontology'" :index="activeImageIndex" />
    <properties-panel class="panel-options" v-show="activePanel === 'properties'" :index="activeImageIndex" />
    <follow-panel class="panel-options" v-show="activePanel === 'follow'" :index="activeImageIndex"  :view="activeImageView"/>
    <review-panel class="panel-options" v-show="activePanel === 'review'" :index="activeImageIndex" />
  </div>

  <calibration-modal
    v-if="image"  :image="image"
    :active.sync="calibrationModal"
    @setResolution="setResolution"
  />

  <!-- Zoom Control Buttons -->
  <div v-if="!loading && activeImageIndex !== null" class="zoom-controls">
    <div class="zoom-buttons">
      <button 
        v-for="zoomLevel in zoomLevels" 
        :key="zoomLevel"
        class="zoom-button"
        @click="setZoomLevel(zoomLevel)"
      >
        {{ zoomLevel }}x
      </button>
    </div>
  </div>

</div>
</template>

<script>
import {get} from '@/utils/store-helpers';

import CytomineImage from './CytomineImage';
// import ImageSelector from './ImageSelector';
// import AppEngineSidebar from '@/components/appengine/sidebar/AppEngineSidebar';

import DrawTools from './DrawTools';
import InformationPanel from './panels/InformationPanel';
import MetadataPanel from './panels/MetadataPanel.vue';
import DigitalZoom from './panels/DigitalZoom';
import ColorManipulation from './panels/ColorManipulation';
import LinkPanel from './panels/LinkPanel';
import LayersPanel from './panels/LayersPanel';
import OntologyPanel from './panels/OntologyPanel';
import PropertiesPanel from './panels/PropertiesPanel';
import FollowPanel from './panels/FollowPanel';
import ReviewPanel from './panels/ReviewPanel';
import RotationPanel from './panels/RotationPanel';
import CalibrationModal from '../image/CalibrationModal.vue';

import viewerModuleModel from '@/store/modules/project_modules/viewer';

import constants from '@/utils/constants.js';
import shortcuts from '@/utils/shortcuts.js';

import {ImageInstance, SliceInstance, Annotation, AnnotationType} from 'cytomine-client';

export default {
  name: 'cytomine-viewer',
  components: {
    CytomineImage,
    // AppEngineSidebar
    DrawTools,
    InformationPanel,
    MetadataPanel,
    DigitalZoom,
    ColorManipulation,
    LinkPanel,
    LayersPanel,
    OntologyPanel,
    PropertiesPanel,
    FollowPanel,
    ReviewPanel,
    RotationPanel,
    CalibrationModal,
  },
  data() {
    return {
      error: false,
      errorBadImageProject: false,
      loading: true,
      reloadInterval: null,
      idViewer: null,
      zoomLevels: [2, 5, 10, 20, 40],
      calibrationModal: false,
    };
  },

  beforeRouteLeave(to, from, next) {
    const currentCaseId = this.projectId || this.$route.params.idCase;

    const isLeavingViewer = !to.path.includes('/image/');

    if (isLeavingViewer) {

      console.log('Leaving viewer, cleaning up...');

      if (currentCaseId) {
        const keyPrefix = `anno-layers-${currentCaseId}-`;
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(keyPrefix)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }

      this.$store.commit('currentProject/resetImageRotations');

      if (this.projectId && this.idViewer && this.$store.state.projects[this.projectId].viewers[this.idViewer]) {
        this.$store.unregisterModule(['projects', this.projectId, 'viewers', this.idViewer]);
        console.log(`[IMS Viewer] Unregistered Vuex module for viewer: ${this.idViewer}`);
      }
    }

    next();
  },


  computed: {
    projectId() {
      return Number(this.$route.params.idCase);
    },
    project: get('currentProject/project'),
    currentUser: get('currentUser/user'),
    viewers() {
      return this.$store.state.projects[this.projectId].viewers;
    },
    idImages() {
      return this.$route.params.idImages.split('-');
    },
    idSlices() {
      return (this.$route.params.idSlices) ? this.$route.params.idSlices.split('-') : [];
    },
    paramIdViewer() {
      return this.$route.query.viewer;
    },
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    viewer() {
      return this.viewers[this.idViewer];
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    image() {
      const wrapper = this.viewerWrapper?.images?.[this.activeImageIndex];
      return wrapper ? wrapper.imageInstance : null;
    },
    indexImages() {
      return this.viewer ? Object.keys(this.viewer.images) : [];
    },
    nbImages() {
      return this.indexImages.length;
    },
    nbHorizontalCells() {
      if (this.nbImages <= 1) return 1;
      if (this.nbImages <= 4) return 2;
      if (this.nbImages <= 9) return 3;
      if (this.nbImages <= 16) return 4;
      return 5;
    },
    nbVerticalCells() {
      return Math.ceil(this.nbImages / this.nbHorizontalCells);
    },
    cells() {
      let cells = new Array(this.nbHorizontalCells*this.nbVerticalCells);
      for(let i = 0; i < this.nbImages; i++) {
        let index = this.indexImages[i];
        let image = this.viewer.images[index].imageInstance;
        let slices = this.viewer.images[index].activeSlices;
        let highlighted = (this.viewer.images[index].view) ? this.viewer.images[index].view.highlighted : false;
        cells[i] = {index, image, slices, highlighted};
      }
      return cells;
    },
    elementHeight() {
      return 100/this.nbVerticalCells;
    },
    elementWidth() {
      return 100/this.nbHorizontalCells;
    },
    shortkeysMapping() {
      let allowed = ['nav-next-image', 'nav-previous-image', 'nav-next-slice', 'nav-previous-slice', 'nav-next-t',
        'nav-previous-t', 'nav-next-c', 'nav-previous-c', 'nav-first-slice', 'nav-last-slice', 'nav-first-t',
        'nav-last-t', 'nav-first-z', 'nav-last-z', 'nav-first-c', 'nav-last-c', 'nav-next-image-in-group',
        'nav-previous-image-in-group', 'nav-next-annot-link', 'nav-previous-annot-link',
        'tool-esc-select', 'tool-select', 'tool-point', 'tool-line', 'tool-freehand-line', 'tool-rectangle', 'tool-circle', 'tool-polygon',
        'tool-freehand-polygon', 'tool-screenshot', 'tool-fill', 'tool-correct-add', 'tool-correct-remove', 'tool-modify', 'tool-rescale',
        'tool-move', 'tool-rotate', 'tool-delete', 'tool-undo', 'tool-redo', 'tool-review-accept', 'tool-review-reject',
        'toggle-review-layer', 'toggle-all-review-layer', 'toggle-selected-layers', 'toggle-all-selected-layers',
        'tool-go-to-slice-t', 'tool-go-to-slice-z', 'tool-go-to-slice-c', 'toggle-information',
        'toggle-zoom', 'toggle-filters', 'toggle-layers', 'toggle-ontology', 'toggle-properties', 'toggle-broadcast',
        'toggle-review', 'toggle-overview', 'toggle-annotations', 'toggle-current', 'toggle-add-image', 'toggle-link',
        'nav-next-z', 'nav-previous-z', 'tool-copy', 'tool-paste', 'tool-review-reject', 'tool-review-toggle',
        'tool-go-to-slice-t', 'tool-go-to-slice-z', 'tool-go-to-slice-c', 'toggle-all-information', 'toggle-all-zoom',
        'toggle-all-filters', 'toggle-all-layers', 'toggle-all-ontology', 'toggle-all-properties',
        'toggle-all-broadcast', 'toggle-all-review', 'toggle-all-overview', 'toggle-all-annotations',
        'toggle-all-current', 'toggle-all-link'];

      return Object.keys(shortcuts).filter(key => allowed.includes(key.replace('viewer-', ''))).reduce((object, key) => {
        object[key.replace('viewer-', '')] = shortcuts[key];
        return object;
      }, {});
    },

    // Shared panels computed properties
    configUI: get('currentProject/configUI'),
    terms() {
      return this.$store.getters['currentProject/terms'];
    },
    activePanel() {
      return this.viewer ? this.viewer.activePanel : null;
    },
    activeImageIndex() {
      return this.viewer ? String(this.viewer.activeImage) : null;
    },
    activeImageWrapper() {
      return this.viewer && this.activeImageIndex !== null ? this.viewer.images[this.activeImageIndex] : null;
    },
    activeImage() {
      return this.activeImageWrapper ? this.activeImageWrapper.imageInstance : null;
    },
    activeImageView() {
      if (!this.activeImageWrapper || !this.activeImageWrapper.view) {
        return null;
      }

      const viewState = this.activeImageWrapper.view;

      return {
        animate: this.animateActiveImageView,
        viewCenter: viewState.center,
        zoom: viewState.zoom,
        rotation: viewState.rotation
      };
    },
    canEditActiveImage() {
      return this.activeImage ? this.$store.getters['currentProject/canEditImage'](this.activeImage) : false;
    },
    layersToPreload() {
      if (!this.activeImageWrapper) return [];
      
      let layers = [];
      let annot = this.activeImageWrapper.selectedAnnotation || this.activeImageWrapper.routedAnnotation;
      if (annot) {
        layers.push(annot.type === AnnotationType.REVIEWED ? -1 : (annot.user ?? annot.annotationLayer));
      }
      if (this.$route.query.action === 'review' && !layers.includes(-1)) {
        layers.push(-1);
      }
      return layers;
    }
  },
  watch: {
    nbImages: {
      handler() {
      // 영상 개수가 변할 때 DOM 업데이트 후 OpenLayers 맵 크기 재계산 강제
        this.$nextTick(() => {
          this.$eventBus.$emit('updateMapSize');
        });
      },
      immediate: true
    },
    paramIdViewer() {
      this.findIdViewer();
    },
    idViewer(_, old) {
      if(old) {
        this.loading = true;
        this.loadViewer();
      }
    },
    viewer() {
      if(!this.viewer) {
        console.log('Viewer closed from external source');
        this.$router.push(`/project/${this.$route.params.idCase}`); // TODO: case 기반으로 변경
      }
    },
    '$route.query.action': {
      handler(action) {
        if (action === 'review') {
          this.handleReviewAction();
        }
      },
      immediate: true // 컴포넌트가 처음 로드될 때도 쿼리에 action=review가 있으면 실행
    },
  },
  methods: {
    setResolution(resolution) {
      this.$store.dispatch(this.viewerModule + 'setImageResolution', {idImage: this.image.id, resolution});
      this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id}); // refresh the sources to update perimeter/area
    },
    findIdViewer() {
      if(this.paramIdViewer) {
        this.idViewer = this.paramIdViewer;
        return;
      }

      for(let id in this.viewers) {
        // if viewer containing the targetted images, and only them, store its id
        let imagesViewer = Object.values(this.viewers[id].images).map(img => img.imageInstance.id).join('-');
        if(imagesViewer === this.$route.params.idImages) {
          this.idViewer = id;
          return;
        }
      }

      this.idViewer = Math.random().toString(36).substr(2, 9);
    },

    closeMap(index) {
      if(this.nbImages === 1) {
        this.$store.unregisterModule(['projects', this.projectId, 'viewers', this.idViewer]);
        this.$router.push(`/project/${this.$route.params.idCase}`); // TODO: case 기반으로 변경, 현 파일 주석 제거
      }
      else {
        this.$store.dispatch(this.viewerModule + 'removeImage', index);
      }
    },

    async loadViewer() {
      try {
        this.$store.commit('currentProject/setCurrentViewer', this.idViewer);
        console.log('inside loadViewer - this.idViewer:', this.idViewer);
        if(!this.viewer) {
          this.$store.registerModule(['projects', this.projectId, 'viewers', this.idViewer], viewerModuleModel);

          // List of unique images (prevent to fetch it multiple times)
          const uniqueIdImages = [...new Set(this.idImages)];
          let uniqueImages = {};
          await Promise.all(uniqueIdImages.map(async id => {
            uniqueImages[id] = await ImageInstance.fetch(id);
          }));

          // Ensure images are in the right project
          const nbWrongProjectImages = Object.values(uniqueImages).filter(image =>
            image.project !== this.projectId
          ).length;
          // const wrongProjectImages = Object.values(uniqueImages).filter(image =>
          //   image.project !== this.projectId
          // );
          if (nbWrongProjectImages > 0) {
            this.errorBadImageProject = true;
            throw new Error('Some images are not from this project');
          }

          // Register images in the viewer, in right order
          let indexedImages = {};
          this.idImages.map(id => {
            indexedImages[this.viewer.indexNextImage] = uniqueImages[id];
            this.$store.dispatch(this.viewerModule + 'registerImage'); // TODO: 에러 [vuex] unknown action type: projects/829/viewers/gphfn24gs/registerImage
          });

          // For each image, initialize them asynchronously, and fetch corresponding slices
          await Promise.all(Object.entries(indexedImages).map(async ([index, image]) => {
            const position = this.idImages.indexOf(String(image.id));
            let idSlices = this.idSlices[position];

            let slices;
            if (idSlices) {
              idSlices = [...new Set(idSlices.split(':'))];
              slices = await Promise.all(idSlices.map(async id => await SliceInstance.fetch(id)));

              // Ensure slices are in the right project/image
              const z = slices[0].zStack;
              const t = slices[0].time;
              const nbWrongSlices = slices.filter(slice =>
                slice.image !== image.id || slice.zStack !== z || slice.time !== t
              ).length;
              if (nbWrongSlices > 0) {
                this.errorBadImageProject = true;
                throw new Error('Some slices are not from this project or cannot be displayed together');
              }
            }
            else {
              slices = [await image.fetchReferenceSlice()];
            }
            await this.$store.dispatch(`${this.viewerModule}images/${index}/initialize`, {image, slices}); // TODO: 에러 [vuex] unknown action type: projects/829/viewers/gphfn24gs/images/0/initialize
          }));

          let images = {};
          //don't fetch multiple times the same image.
          let idImages = [...new Set(this.idImages)];
          await Promise.all(idImages.map(async id => {
            let image = await ImageInstance.fetch(id);
            images[id] = image;
          }));
          const imagesNotInCurrentProject = Object.values(images).filter(image => image.project != this.projectId);
          if (imagesNotInCurrentProject.length > 0) {
            this.errorBadImageProject = true;
            throw new Error('Some images are not from this project');
          }
        }
        else {
          await this.$store.dispatch(this.viewerModule + 'refreshData');
        }
        this.loading = false;
        // 로딩이 끝난 후, 현재 URL에 review 액션이 있다면 실행해줍니다.
        if (this.$route.query.action === 'review') {
          this.handleReviewAction();
        }
      }
      catch(err) {
        console.log(err);
        this.error = true;
      }
    },

    async selectAnnotationHandler({index, annot, center=false}) {
      try {
        if (index && annot.image !== this.viewer.images[index].imageInstance.id) {
          annot = await Annotation.fetch(annot.id);
          let [image, slice] = await Promise.all([
            ImageInstance.fetch(annot.image),
            SliceInstance.fetch(annot.slice)
          ]);
          this.$store.commit(`${this.viewerModule}images/${index}/setRoutedAnnotation`, annot);
          await this.$store.dispatch(`${this.viewerModule}images/${index}/setImageInstance`, {image, slices: [slice]});
        }
        else if (index === null) {
          annot = await Annotation.fetch(annot.id);
          if (this.idImages.includes(String(annot.image))) {
            let index = this.cells.find(cell => cell.image.id === annot.image).index;
            this.$eventBus.$emit('selectAnnotation', {index, annot, center});
          }
          else {
            let [image, slice] = await Promise.all([
              ImageInstance.fetch(annot.image),
              SliceInstance.fetch(annot.slice)
            ]);
            await this.$store.dispatch(this.viewerModule + 'addImage', {image, slices: [slice], annot});
          }
        }
      }
      catch(err) {
        console.log(err);
        this.error = true;
      }
    },

    shortkeyEvent(event) {
      let key = event.srcKey;
      
      // Handle panel toggle shortcuts
      if(key.startsWith('toggle-') || key.startsWith('toggle-all-')) {
        this.handlePanelShortkey(key);
        return;
      }

      this.$eventBus.$emit('shortkeyEvent', event.srcKey);
    },

    async handleReviewAction() {
      const index = this.activeImageIndex;
      if (!index || !this.activeImageWrapper) return;

      const image = this.activeImage; // Computed: 현재 활성화된 이미지 인스턴스

      // [STEP 1] 리뷰 패널을 즉시 엽니다. (사용자 피드백)
      this.$store.commit(this.viewerModule + 'setActivePanel', 'review');

      // [STEP 2] 이미 리뷰가 완료되었거나 진행 중인지 확인합니다.
      if (image.reviewed) {
        return; // 이미 완료된 경우 패널만 보여주고 종료
      }

      try {
        if (!image.inReview) {
          let clone = await image.clone().review(); 
          this.$store.commit(`${this.viewerModule}images/${index}/setImageInstance`, clone);
        }
        this.$store.commit(`${this.viewerModule}images/${index}/setReviewMode`, true);

      }
      catch (error) {
        console.error('When review, error occured:', error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-start-review')
        });
      }
    },

    handlePanelShortkey(key) {
      key = key.replace('toggle-all-', 'toggle-');
      switch(key) {
        case 'toggle-information':
          if (this.isPanelDisplayed('info')){
            this.togglePanel('info');
          }
          return;
        case 'toggle-zoom':
          if (this.isPanelDisplayed('digital-zoom')) {
            this.togglePanel('digital-zoom');
          }
          return;
        case 'rotation-panel':
          if (this.isPanelDisplayed('rotation-panel')) {
            this.togglePanel('rotation-panel');
          }
          return;
        case 'toggle-link':
          if (this.isPanelDisplayed('link') && this.nbImages > 1) {
            this.togglePanel('link');
          }
          return;
        case 'toggle-filters':
          if (this.isPanelDisplayed('color-manipulation')) {
            this.togglePanel('colors');
          }
          return;
        case 'toggle-layers':
          if (this.isPanelDisplayed('image-layers')) {
            this.togglePanel('layers');
          }
          return;
        case 'toggle-ontology':
          if (this.isPanelDisplayed('ontology') && this.terms && this.terms.length > 0) {
            this.togglePanel('ontology');
          }
          return;
        case 'toggle-properties':
          if (this.isPanelDisplayed('property')) {
            this.togglePanel('properties');
          }
          return;
        case 'toggle-broadcast':
          if (this.isPanelDisplayed('follow')) {
            this.togglePanel('follow');
          }
          return;
        case 'toggle-review':
          if (this.isPanelDisplayed('review') && this.canEditActiveImage) {
            this.togglePanel('review');
          }
          return;
      }
    },

    // Shared panels methods
    togglePanel(panel) {
      this.$store.commit(this.viewerModule + 'togglePanel', panel);
    },

    isPanelDisplayed(panel) {
      return this.configUI[`project-explore-${panel}`];
    },

    resetZoom() {
      if (this.activeImageWrapper) {
        this.$eventBus.$emit('resetZoom', {index: this.activeImageIndex});
      }
    },

    fitZoom() {
      if (this.activeImageWrapper) {
        this.$eventBus.$emit('fitZoom', {index: this.activeImageIndex});
      }
    },

    animateActiveImageView(options) {
      if (this.activeImageWrapper) {
        this.$eventBus.$emit('animateView', {index: this.activeImageIndex, options});
      }
    },

    closeViewer() {
      // 1. 이미지가 하나뿐일 때: 뷰어 종료 및 페이지 이동
      if (this.nbImages === 1) {
        this.$store.unregisterModule(['projects', this.projectId, 'viewers', this.idViewer]);
        
        const savedHierarchy = JSON.parse(localStorage.getItem('worklistHierarchy') || '{}');
        const { sortPropWorklist, idWorklist } = savedHierarchy;
        const caseId = this.$route.params.idCase;

        this.$router.push(`/worklist/${sortPropWorklist}-${idWorklist}/case/${caseId}?tab=images`);
        return;
      }

      // 2. 여러 이미지 중 현재 활성화된 이미지를 닫을 때
      const targetImageId = this.activeImageIndex;
      const targetIdx = this.indexImages.indexOf(targetImageId);

      if (targetIdx < 0) {
        console.error('incorrect index', { targetImageId, indexImages: this.indexImages });
        return;
      }

      // 다음 활성화할 이미지 결정 (현재가 첫 번째면 다음 이미지, 아니면 이전 이미지)
      const nextActiveImageId = targetIdx > 0 ? this.indexImages[targetIdx - 1] : this.indexImages[1];

      // Store 상태 업데이트
      this.$store.commit(`${this.viewerModule}setActiveImage`, nextActiveImageId);
      this.$store.dispatch(`${this.viewerModule}removeImage`, targetImageId);
    },

    setZoomLevel(level) {
      if (this.activeImageWrapper) {
        this.$eventBus.$emit('setZoomLevel', {index: this.activeImageIndex, level});
      }
    },

    takeScreenshot() {
      if (this.activeImageWrapper) {
        this.$eventBus.$emit('takeScreenshot', {index: this.activeImageIndex});
      }
    }
  },
  async created() {
    if (this.$route.params.idCase) { // case 목록 조회 후, 현재 선택된 case가 있으면 로드
      await this.$store.dispatch('currentProject/loadProject', this.$route.params.idCase);
    }

    this.findIdViewer();
    await this.loadViewer();
    this.reloadInterval = setInterval(
      () => this.$eventBus.$emit('reloadAnnotations'),
      constants.VIEWER_ANNOTATIONS_REFRESH_INTERVAL
    );
  },
  mounted() {
    this.$eventBus.$on('selectAnnotation', this.selectAnnotationHandler);
  },
  beforeDestroy() {
    console.log('before destry test');
    this.$eventBus.$off('selectAnnotation', this.selectAnnotationHandler);
    clearInterval(this.reloadInterval);
  }
};
</script>

<style lang="scss" scoped>
$backgroundPanelBar: #555;
$widthPanelBar: 2.8rem;
$backgroundPanel: #f2f2f2;
$colorPanelLink: #eee;
$colorHoverPanelLink: white;
$colorBorderPanelLink: #222;
$colorOpenedPanelLink: #6c95c8;

.cytomine-viewer {
  display: flex;
  height: 100%;
}

.ae-sidebar {
  width: 24rem;
  border-right-color: #333;
  border-right-width: 1px;
  border-right-style: solid;
}

.maps-wrapper {
  flex: 1;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
}

.map-cell {
  overflow: visible;
  /* 뷰잉 컴포넌트 상단 검은 선이었던 것 */
  // border-top: 0.2em solid #ffffff;
  // overflow: hidden;
}

.hidden {
  display: none;
}

.highlighted {
  border: 6px solid #0099ff;
}

.draw-tools-floating {
  position: fixed;
  right: 80px; // Position to the left of the floating-toolbar
  top: 50%;
  transform: translateY(-50%);
  z-index: 30;
  
  // Responsive design for smaller screens
  @media (max-width: 768px) {
    right: 60px;
  }
}

.floating-toolbar {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px;
  z-index: 30;
  backdrop-filter: blur(8px);
  
  // Responsive design for smaller screens
  @media (max-width: 768px) {
    right: 10px;
    padding: 6px;
    gap: 8px;
  }
}

.toolbar-button {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.2rem;
  color: #555;
  background: transparent;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
    transform: scale(1.05);
  }
  
  &.active {
    background: #6c95c8;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &.close-button {
    color: #ff6b6b;
    
    &:hover {
      background: rgba(255, 107, 107, 0.1);
      color: #e74c3c;
    }
  }
  
  // Responsive button size for smaller screens
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }
}

.panel-options {
  position: fixed;
  top: 50%;
  right: 80px; // Position next to the floating toolbar
  transform: translateY(-50%);
  width: 24em;
  min-height: 10em;
  max-height: 70vh;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75em;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 999;
  backdrop-filter: blur(8px);

  h1 {
    font-size: 1.1rem;
    padding-top: 0.3rem !important;
    padding-bottom: 1rem !important;
  }

  table {
    background: none;
    width: 100%;
  }
  
  // Responsive positioning for smaller screens
  @media (max-width: 768px) {
    right: 60px;
    width: 20em;
    max-height: 60vh;
  }
  
  @media (max-width: 480px) {
    right: 10px;
    left: 10px;
    width: auto;
    top: auto;
    bottom: 80px;
    transform: none;
    max-height: 50vh;
  }
}

.panel-metadata {
  position: fixed;
  top: 0vh;
  right: 80px;
  min-width: 30em;
  max-width: 40em;
  min-height: 30em;
  max-height: 80vh;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75em;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(8px);
  z-index: 999;
  
  // Responsive positioning for smaller screens
  @media (max-width: 768px) {
    right: 60px;
    min-width: 25em;
    max-width: 30em;
  }
  
  @media (max-width: 480px) {
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
    width: auto;
    top: 10px;
    max-height: 70vh;
  }
}

.zoom-controls {
  position: absolute;
  right: 11rem;
  bottom: 1.5rem;
  z-index: 30;
  
  // Responsive design for smaller screens
  @media (max-width: 768px) {
    right: 10px;
  }
}

.zoom-buttons {
  display: flex;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 6px 8px;
  backdrop-filter: blur(8px);
  gap: 4px;
}

.zoom-button {
  border: none;
  background: #444;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 36px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #666;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  // Responsive button size for smaller screens
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 5px 8px;
    min-width: 32px;
    height: 28px;
  }
}
</style>
